// 2026-09-23 - FormuLearning - 추천 처방 카드 (COMP-RECOMMENDATION-CARD, 이슈 #67 S4-7)
// 2026-11-02 수정 - 이슈 #101(M1002-09): 성분 비율 `%` 를 둘째 자리 + 합계 100.00 보정(`lib/composition.ts`).
// 파일 위치: frontend/src/features/step4/RecommendationCard.tsx
// 목적: 처방 하나당 카드 하나(UI/UX v2.7 §4.7 · M84-32·67 · M915-22). 순서는 정본 와이어 그대로
//       ① 성분 비율(`mg/T`·`%`, 성분표와 같은 표기 — 성분명은 `Q-260813-24` 미확정이라 저장값 그대로)
//       ② 예측값 평균 ± σ(추천 시점 고정, BR-OPT-14) + target 목표 병기
//       ③ 예측 용출 그래프 — 최신 분석 `profiles[batch_id]`(D-S4-3), 기준 대조약 점선. 분석이 없으면 «예측 계산 중»
//       ④ AI 해설 — 추천 시점 예측 기준(BR-OPT-15). 비면 «해설을 만들지 못했습니다»(작업계획 D11)

import { Fragment, useEffect, useRef } from 'react'
import type { Step4Run } from '../../api/step4'
import type { PhProfile, XVariable, YVariable } from '../../api/step3'
import { useUiStore } from '../../stores/uiStore'
import { chartTokens } from '../../lib/chartTheme'
import { compareSolutionKeys, solutionKey, solutionLabel } from '../../lib/dissolution'
import MarkdownText from '../../components/feedback/MarkdownText'
import { NON_WEIGHT_MODE, pctDisplay } from '../../lib/composition'

type ProfilePoint = { time: number; mean: number; std: number }

/** 저장 정밀도(성분표 `round3` 와 같은 규칙, M84-09). */
const round3 = (n: number) => Math.round(n * 1000) / 1000
const fmt3 = (v: number | null) => (v != null && Number.isFinite(v) ? String(round3(v)) : '—')

/** 목표값 — 문자열의 첫 수치(백엔드 `parse_target_value` 와 같은 규칙, BR-OPT-03). */
function parseTarget(y: YVariable | undefined): number | null {
  if (!y) return null
  if (typeof y.target_value === 'number' && Number.isFinite(y.target_value)) return y.target_value
  const m = /-?\d+(?:\.\d+)?/.exec(String(y.constraints ?? ''))
  return m ? Number(m[0]) : null
}

/** 예측값 표의 행 — `_source` 같은 메타 키와 `{mean,std}` 가 아닌 값(이관분)은 뺀다(작업계획 D17). */
function predictedRows(p: Step4Run['predicted']): [string, { mean: number; std: number }][] {
  if (!p) return []
  return Object.entries(p).filter((e): e is [string, { mean: number; std: number }] =>
    !e[0].startsWith('_') && typeof e[1] === 'object' && e[1] != null
      && typeof (e[1] as { mean?: unknown }).mean === 'number')
}

/** 이름 · 값 · 보조 3칸 — 이름이 남는 폭을 다 쓰고 넘치면 말줄임(전체는 title). */
const GRID3 = { gridTemplateColumns: 'minmax(0, 1fr) auto auto' }

const SOLUTION_COLORS = (t: ReturnType<typeof chartTokens>) => [t.teal, t.amber, t.red, t.purple]

function PredictionChart({ profile, referenceProfiles }: {
  profile: Record<string, ProfilePoint[]>
  referenceProfiles: PhProfile[]
}) {
  const theme = useUiStore((s) => s.theme)
  const div = useRef<HTMLDivElement>(null)
  const keys = Object.keys(profile).sort(compareSolutionKeys)

  useEffect(() => {
    if (!div.current) return
    let cancelled = false
    import('plotly.js-dist-min').then((Plotly) => {
      if (cancelled || !div.current) return
      const t = chartTokens()
      const colors = SOLUTION_COLORS(t)
      const traces: Record<string, unknown>[] = []
      let peak = 100
      keys.forEach((key, i) => {
        const color = colors[i % colors.length]
        const pts = [...profile[key]].sort((a, b) => a.time - b.time)
        const x = pts.map((p) => p.time)
        const up = pts.map((p) => p.mean + p.std)
        const lo = pts.map((p) => p.mean - p.std)
        peak = Math.max(peak, ...up)
        // ±σ 띠 — 위 경계를 가고 아래 경계로 돌아오는 닫힌 도형
        traces.push({
          x: [...x, ...[...x].reverse()], y: [...up, ...[...lo].reverse()],
          type: 'scatter', mode: 'lines', fill: 'toself', fillcolor: color, opacity: 0.18,
          line: { width: 0 }, hoverinfo: 'skip', showlegend: false,
        })
        traces.push({
          x, y: pts.map((p) => p.mean), customdata: pts.map((p) => p.std),
          type: 'scatter', mode: 'lines+markers', name: solutionLabel(key),
          line: { color, width: 2 }, marker: { color, size: 4 },
          hovertemplate: `${solutionLabel(key)} 예측 %{x}min<br>%{y:.1f}% ± %{customdata:.1f}<extra></extra>`,
        })
        const ref = referenceProfiles.find((r) => solutionKey(r.ph) === key)
        const refPts = (ref?.time_points ?? []).filter((d) => d.dissolution != null && Number.isFinite(d.time))
          .sort((a, b) => a.time - b.time)
        if (refPts.length > 0) {
          traces.push({
            x: refPts.map((d) => d.time), y: refPts.map((d) => d.dissolution),
            type: 'scatter', mode: 'lines', name: `${solutionLabel(key)} 대조약`, showlegend: false,
            line: { color, width: 1.5, dash: 'dot' },
            hovertemplate: `${solutionLabel(key)} 대조약 %{x}min<br>%{y:.1f}%<extra></extra>`,
          })
        }
      })
      Plotly.newPlot(div.current, traces, {
        paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: t.plotBg,
        font: { color: t.font, size: 9 },
        margin: { t: 22, r: 8, b: 28, l: 32 },
        xaxis: { gridcolor: t.grid, title: { text: 'min', font: { size: 9 } }, rangemode: 'tozero' },
        yaxis: { gridcolor: t.grid, title: { text: '%', font: { size: 9 } }, range: [0, peak <= 100 ? 105 : Math.ceil((peak + 5) / 5) * 5] },
        showlegend: true,
        legend: { orientation: 'h', y: 1.18, x: 0, font: { size: 8 } },
      }, { displayModeBar: false, responsive: true })
    })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(profile), JSON.stringify(referenceProfiles), theme])

  return <div ref={div} style={{ width: '100%', height: 190 }} />
}

interface Props {
  batch: Step4Run
  xVariables: XVariable[]
  totalWeightMg: number | null
  yVariables: YVariable[]
  /** 최신 분석의 이 배치 예측 용출. */
  profile: Record<string, ProfilePoint[]> | undefined
  /** 프로파일이 없을 때 — 분석이 아직 없거나 재계산 중이면 곧 생긴다(`pending`), 끝난 분석에도 없으면 `none`. */
  profileMissing: 'pending' | 'none'
  /** 그 라운드가 추천 시 쓴 기준 대조약 프로파일(작업계획 D14). */
  referenceProfiles: PhProfile[]
  /** 그 라운드 추천이 해설 단계를 거쳤는가(`round.meta.ai_summary` 유무) — 빈 해설 문구를 가른다(D11). */
  hadAiStep: boolean
}

export default function RecommendationCard({
  batch, xVariables, totalWeightMg, yVariables, profile, profileMissing, referenceProfiles, hadAiStep,
}: Props) {
  const total = totalWeightMg && totalWeightMg > 0 ? totalWeightMg : null
  const byName = new Map(xVariables.map((x) => [x.excipient.trim().toLowerCase(), x]))
  // 성분 순서는 Step 3 X 표 순서, X 표에 없는 키는 뒤에(이관분)
  const keys = [
    ...xVariables.map((x) => x.excipient).filter((k) => k in batch.recommended_x),
    ...Object.keys(batch.recommended_x).filter((k) => !byName.has(k.trim().toLowerCase())),
  ]
  const yByName = new Map(yVariables.map((y) => [y.response, y]))
  const pct2 = pctDisplay(batch.recommended_x, (k) => byName.get(k.trim().toLowerCase())?.mode === NON_WEIGHT_MODE)
  const preds = predictedRows(batch.predicted)
  const hasProfile = profile != null && Object.keys(profile).length > 0
  const text = batch.ai_commentary_ko?.trim()

  return (
    <article
      className="flex min-w-0 flex-col gap-2 rounded border p-2.5"
      style={{ borderColor: 'var(--border-purple)', background: 'var(--color-bg-card)' }}
      data-testid={`rec-card-${batch.batch_code}`}
    >
      <span className="self-start rounded px-1.5 py-0.5 text-[11px] font-semibold"
            style={{ background: 'var(--bg-chip-purple)', color: 'var(--color-accent-purple)' }}>
        {batch.batch_code}
      </span>

      {/* ① 성분 비율 — 성분표와 같은 규칙: 비율 %·mg/T, 공정변수(non-weight)는 mg 만, balance 는 계산값 */}
      <div>
        <p className="mb-0.5 text-[10px] font-semibold text-text-secondary">성분 비율</p>
        <div className="grid gap-x-2 text-[10px]" style={GRID3} data-testid="rec-ratio">
          <span className="text-text-muted">성분</span>
          <span className="text-right text-text-muted">mg/T</span>
          <span className="text-right text-text-muted">%</span>
          {keys.map((k) => {
            const pct = batch.recommended_x[k]
            const nonWeight = byName.get(k.trim().toLowerCase())?.mode === 'selected(non-weight)'
            const mg = typeof pct === 'number' && total ? (pct * total) / 100 : null
            return (
              <Fragment key={k}>
                <span className="truncate text-text-secondary" title={k}>{k}</span>
                <span className="whitespace-nowrap text-right tabular-nums text-text-body">
                  {fmt3(mg)}{nonWeight && mg != null ? ' mg' : ''}
                </span>
                <span className="text-right tabular-nums text-text-body"
                      title={nonWeight ? 'selected(non-weight) — mg 로만 관리합니다' : undefined}>
                  {nonWeight ? '—' : (pct2.shown[k] ?? fmt3(typeof pct === 'number' ? pct : null))}
                </span>
              </Fragment>
            )
          })}
        </div>
      </div>

      {/* ② 예측값 — 추천 시점 고정(BR-OPT-14). target 방향은 목표를 병기 */}
      {preds.length > 0 && (
        <div>
          <p className="mb-0.5 text-[10px] font-semibold text-text-secondary">예측값 (평균 ± σ)</p>
          <div className="grid gap-x-2 text-[10px]" style={GRID3} data-testid="rec-predicted">
            {preds.map(([obj, v]) => {
              const y = yByName.get(obj)
              const target = y?.target === 'target' ? parseTarget(y) : null
              return (
                <Fragment key={obj}>
                  <span className="truncate text-text-secondary" title={obj}>{obj}</span>
                  <span className="whitespace-nowrap text-right tabular-nums text-text-body">
                    {v.mean.toFixed(1)} ± {v.std.toFixed(1)}
                  </span>
                  <span className="whitespace-nowrap text-right text-text-muted">
                    {target != null ? `목표 ${target}` : ''}
                  </span>
                </Fragment>
              )
            })}
          </div>
        </div>
      )}

      {/* ③ 예측 용출 — 최신 분석(D-S4-3). 모델 예측임을 표기 */}
      <div data-testid="rec-profile">
        <p className="text-[10px] font-semibold text-text-secondary">
          예측 용출 프로파일 <span className="font-normal text-text-muted">· 모델 예측 (실선 ±σ) · 점선 = 기준 대조약</span>
        </p>
        {hasProfile
          ? <PredictionChart profile={profile!} referenceProfiles={referenceProfiles} />
          : profileMissing === 'pending'
            ? <p className="py-6 text-center text-[10px] text-text-muted" data-testid="rec-profile-pending">⟳ 예측 계산 중</p>
            // 끝난 분석에 이 배치가 없다 — 조성이 X 정의와 맞지 않는 이관 배치 등. «계산 중» 으로 두면 끝나지 않는다
            : <p className="py-6 text-center text-[10px] text-text-muted" data-testid="rec-profile-none">최신 분석에 이 처방의 예측 프로파일이 없습니다</p>}
      </div>

      {/* ④ AI 해설 — 한국어 직접 생성, hover 없음(D-S4-4). 추천 시점 예측 기준(BR-OPT-15) */}
      <div className="rounded border p-2" style={{ borderColor: 'var(--border-teal)', background: 'var(--bg-chip-teal)' }}
           data-testid="rec-commentary">
        <p className="mb-1 flex flex-wrap items-center gap-x-2 text-[10px] font-semibold text-accent-teal">
          AI 해설 <span className="font-normal text-text-muted">추천 시점 예측 기준</span>
        </p>
        {text
          ? <>
              <MarkdownText text={text} />
              <p className="mt-1 text-[9px] italic text-text-secondary">⚠ AI 생성 내용 — 검토 후 사용</p>
            </>
          : <p className="text-[11px] text-text-secondary" data-testid="rec-commentary-empty">
              {hadAiStep ? '해설을 만들지 못했습니다' : '이 라운드는 AI 해설 도입 전 추천이라 해설이 없습니다'}
            </p>}
      </div>
    </article>
  )
}
